/**
 * Integration Monitoring Service
 * Handles real-time monitoring, metrics collection, and alerting for integrations
 */

import { EventEmitter } from 'events';

// Types for monitoring data
export interface IntegrationHealth {
  id: string;
  name: string;
  type: 'crm' | 'ecommerce' | 'social';
  platform: string;
  status: 'healthy' | 'warning' | 'error' | 'offline';
  healthScore: number;
  lastSync: Date;
  syncSuccess: number;
  errorRate: number;
  responseTime: number;
  uptime: number;
  recordsProcessed: number;
  recordsPerHour: number;
  lastHealthCheck: Date;
}

export interface SyncOperation {
  id: string;
  integrationId: string;
  integrationName: string;
  type: 'full_sync' | 'incremental_sync' | 'real_time_update';
  direction: 'bidirectional' | 'to_platform' | 'from_platform';
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  startedAt: Date;
  completedAt?: Date;
  duration?: number;
  recordsProcessed: number;
  recordsSucceeded: number;
  recordsFailed: number;
  errors: SyncError[];
  progress?: number;
  metadata?: Record<string, any>;
}

export interface SyncError {
  id: string;
  timestamp: Date;
  level: 'error' | 'warning' | 'info';
  message: string;
  details?: string;
  recordId?: string;
  field?: string;
  resolution?: string;
  resolved: boolean;
  integrationId: string;
  operationId?: string;
}

export interface PerformanceMetrics {
  totalIntegrations: number;
  activeIntegrations: number;
  healthyIntegrations: number;
  totalSyncs: number;
  successfulSyncs: number;
  failedSyncs: number;
  averageResponseTime: number;
  recordsProcessedToday: number;
  errorRate: number;
  uptime: number;
  timestamp: Date;
}

export interface AlertRule {
  id: string;
  name: string;
  condition: string;
  threshold: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  enabled: boolean;
  integrationId?: string; // Optional - for integration-specific alerts
  lastTriggered?: Date;
  triggerCount: number;
  cooldownPeriod: number; // minutes
}

export interface Alert {
  id: string;
  ruleId: string;
  integrationId?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  message: string;
  timestamp: Date;
  acknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: Date;
  resolved: boolean;
  resolvedAt?: Date;
  metadata?: Record<string, any>;
}

export interface MonitoringConfig {
  healthCheckInterval: number; // milliseconds
  metricsRetentionDays: number;
  alertCooldownMinutes: number;
  maxErrorsPerHour: number;
  responseTimeThreshold: number; // milliseconds
  uptimeThreshold: number; // percentage
}

export class IntegrationMonitoringService extends EventEmitter {
  private static instance: IntegrationMonitoringService;
  private config: MonitoringConfig;
  private healthData: Map<string, IntegrationHealth> = new Map();
  private syncOperations: Map<string, SyncOperation> = new Map();
  private errors: Map<string, SyncError> = new Map();
  private alertRules: Map<string, AlertRule> = new Map();
  private activeAlerts: Map<string, Alert> = new Map();
  private metricsHistory: PerformanceMetrics[] = [];
  private healthCheckTimer?: NodeJS.Timeout;
  private metricsTimer?: NodeJS.Timeout;

  private constructor(config: Partial<MonitoringConfig> = {}) {
    super();
    this.config = {
      healthCheckInterval: 30000, // 30 seconds
      metricsRetentionDays: 30,
      alertCooldownMinutes: 15,
      maxErrorsPerHour: 10,
      responseTimeThreshold: 1000,
      uptimeThreshold: 95,
      ...config
    };
    
    this.startMonitoring();
  }

  static getInstance(config?: Partial<MonitoringConfig>): IntegrationMonitoringService {
    if (!IntegrationMonitoringService.instance) {
      IntegrationMonitoringService.instance = new IntegrationMonitoringService(config);
    }
    return IntegrationMonitoringService.instance;
  }

  /**
   * Start monitoring services
   */
  private startMonitoring(): void {
    // Health check timer
    this.healthCheckTimer = setInterval(() => {
      this.performHealthChecks();
    }, this.config.healthCheckInterval);

    // Metrics collection timer
    this.metricsTimer = setInterval(() => {
      this.collectMetrics();
    }, 60000); // Every minute

    console.log('Integration monitoring service started');
  }

  /**
   * Stop monitoring services
   */
  stopMonitoring(): void {
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
    }
    if (this.metricsTimer) {
      clearInterval(this.metricsTimer);
    }
    console.log('Integration monitoring service stopped');
  }

  /**
   * Register an integration for monitoring
   */
  registerIntegration(integration: Partial<IntegrationHealth>): void {
    const health: IntegrationHealth = {
      id: integration.id!,
      name: integration.name!,
      type: integration.type!,
      platform: integration.platform!,
      status: 'healthy',
      healthScore: 100,
      lastSync: new Date(),
      syncSuccess: 100,
      errorRate: 0,
      responseTime: 0,
      uptime: 100,
      recordsProcessed: 0,
      recordsPerHour: 0,
      lastHealthCheck: new Date(),
      ...integration
    };

    this.healthData.set(integration.id!, health);
    this.emit('integration_registered', health);
    console.log(`Registered integration for monitoring: ${integration.name}`);
  }

  /**
   * Unregister an integration from monitoring
   */
  unregisterIntegration(integrationId: string): void {
    this.healthData.delete(integrationId);
    this.emit('integration_unregistered', integrationId);
    console.log(`Unregistered integration from monitoring: ${integrationId}`);
  }

  /**
   * Record a sync operation
   */
  recordSyncOperation(operation: Partial<SyncOperation>): string {
    const syncOp: SyncOperation = {
      id: operation.id || this.generateId(),
      integrationId: operation.integrationId!,
      integrationName: operation.integrationName!,
      type: operation.type!,
      direction: operation.direction!,
      status: operation.status || 'pending',
      startedAt: operation.startedAt || new Date(),
      recordsProcessed: operation.recordsProcessed || 0,
      recordsSucceeded: operation.recordsSucceeded || 0,
      recordsFailed: operation.recordsFailed || 0,
      errors: operation.errors || [],
      ...operation
    };

    this.syncOperations.set(syncOp.id, syncOp);
    this.emit('sync_operation_recorded', syncOp);
    
    // Update integration health based on sync operation
    this.updateIntegrationHealth(syncOp.integrationId, syncOp);
    
    return syncOp.id;
  }

  /**
   * Update sync operation status
   */
  updateSyncOperation(operationId: string, updates: Partial<SyncOperation>): void {
    const operation = this.syncOperations.get(operationId);
    if (!operation) {
      console.warn(`Sync operation not found: ${operationId}`);
      return;
    }

    const updatedOperation = { ...operation, ...updates };
    
    // Calculate duration if completed
    if (updates.status === 'completed' || updates.status === 'failed') {
      updatedOperation.completedAt = updates.completedAt || new Date();
      updatedOperation.duration = updatedOperation.completedAt.getTime() - operation.startedAt.getTime();
    }

    this.syncOperations.set(operationId, updatedOperation);
    this.emit('sync_operation_updated', updatedOperation);
    
    // Update integration health
    this.updateIntegrationHealth(operation.integrationId, updatedOperation);
  }

  /**
   * Record an error
   */
  recordError(error: Partial<SyncError>): string {
    const syncError: SyncError = {
      id: error.id || this.generateId(),
      timestamp: error.timestamp || new Date(),
      level: error.level || 'error',
      message: error.message!,
      details: error.details,
      recordId: error.recordId,
      field: error.field,
      resolution: error.resolution,
      resolved: error.resolved || false,
      integrationId: error.integrationId!,
      operationId: error.operationId,
      ...error
    };

    this.errors.set(syncError.id, syncError);
    this.emit('error_recorded', syncError);
    
    // Check if this error should trigger an alert
    this.checkAlertConditions(syncError.integrationId, 'error_recorded', syncError);
    
    return syncError.id;
  }

  /**
   * Resolve an error
   */
  resolveError(errorId: string, resolution: string): void {
    const error = this.errors.get(errorId);
    if (!error) {
      console.warn(`Error not found: ${errorId}`);
      return;
    }

    error.resolved = true;
    error.resolution = resolution;
    this.errors.set(errorId, error);
    this.emit('error_resolved', error);
  }

  /**
   * Get integration health data
   */
  getIntegrationHealth(integrationId?: string): IntegrationHealth[] {
    if (integrationId) {
      const health = this.healthData.get(integrationId);
      return health ? [health] : [];
    }
    return Array.from(this.healthData.values());
  }

  /**
   * Get sync operations
   */
  getSyncOperations(integrationId?: string, limit: number = 50): SyncOperation[] {
    let operations = Array.from(this.syncOperations.values());
    
    if (integrationId) {
      operations = operations.filter(op => op.integrationId === integrationId);
    }
    
    // Sort by start time (most recent first)
    operations.sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime());
    
    return operations.slice(0, limit);
  }

  /**
   * Get errors
   */
  getErrors(integrationId?: string, resolved?: boolean, limit: number = 100): SyncError[] {
    let errors = Array.from(this.errors.values());
    
    if (integrationId) {
      errors = errors.filter(error => error.integrationId === integrationId);
    }
    
    if (resolved !== undefined) {
      errors = errors.filter(error => error.resolved === resolved);
    }
    
    // Sort by timestamp (most recent first)
    errors.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    
    return errors.slice(0, limit);
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics(): PerformanceMetrics {
    const integrations = Array.from(this.healthData.values());
    const operations = Array.from(this.syncOperations.values());
    const _errors = Array.from(this.errors.values());
    
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    
    // Filter operations from last 24 hours
    const recentOperations = operations.filter(op => op.startedAt >= oneDayAgo);
    const completedOperations = recentOperations.filter(op => op.status === 'completed');
    const failedOperations = recentOperations.filter(op => op.status === 'failed');
    
    // Calculate metrics
    const totalIntegrations = integrations.length;
    const activeIntegrations = integrations.filter(i => i.status !== 'offline').length;
    const healthyIntegrations = integrations.filter(i => i.status === 'healthy').length;
    const totalSyncs = recentOperations.length;
    const successfulSyncs = completedOperations.length;
    const failedSyncs = failedOperations.length;
    
    const averageResponseTime = integrations.length > 0 
      ? integrations.reduce((sum, i) => sum + i.responseTime, 0) / integrations.length 
      : 0;
    
    const recordsProcessedToday = recentOperations.reduce((sum, op) => sum + op.recordsProcessed, 0);
    const errorRate = totalSyncs > 0 ? (failedSyncs / totalSyncs) * 100 : 0;
    
    const uptime = integrations.length > 0 
      ? integrations.reduce((sum, i) => sum + i.uptime, 0) / integrations.length 
      : 100;

    return {
      totalIntegrations,
      activeIntegrations,
      healthyIntegrations,
      totalSyncs,
      successfulSyncs,
      failedSyncs,
      averageResponseTime: Math.round(averageResponseTime),
      recordsProcessedToday,
      errorRate: Math.round(errorRate * 100) / 100,
      uptime: Math.round(uptime * 100) / 100,
      timestamp: now
    };
  }

  /**
   * Add alert rule
   */
  addAlertRule(rule: Partial<AlertRule>): string {
    const alertRule: AlertRule = {
      id: rule.id || this.generateId(),
      name: rule.name!,
      condition: rule.condition!,
      threshold: rule.threshold!,
      severity: rule.severity || 'medium',
      enabled: rule.enabled !== false,
      integrationId: rule.integrationId,
      triggerCount: 0,
      cooldownPeriod: rule.cooldownPeriod || this.config.alertCooldownMinutes,
      ...rule
    };

    this.alertRules.set(alertRule.id, alertRule);
    this.emit('alert_rule_added', alertRule);
    
    return alertRule.id;
  }

  /**
   * Get alert rules
   */
  getAlertRules(): AlertRule[] {
    return Array.from(this.alertRules.values());
  }

  /**
   * Get active alerts
   */
  getActiveAlerts(): Alert[] {
    return Array.from(this.activeAlerts.values())
      .filter(alert => !alert.resolved)
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }

  /**
   * Acknowledge alert
   */
  acknowledgeAlert(alertId: string, acknowledgedBy: string): void {
    const alert = this.activeAlerts.get(alertId);
    if (!alert) {
      console.warn(`Alert not found: ${alertId}`);
      return;
    }

    alert.acknowledged = true;
    alert.acknowledgedBy = acknowledgedBy;
    alert.acknowledgedAt = new Date();
    
    this.activeAlerts.set(alertId, alert);
    this.emit('alert_acknowledged', alert);
  }

  /**
   * Resolve alert
   */
  resolveAlert(alertId: string): void {
    const alert = this.activeAlerts.get(alertId);
    if (!alert) {
      console.warn(`Alert not found: ${alertId}`);
      return;
    }

    alert.resolved = true;
    alert.resolvedAt = new Date();
    
    this.activeAlerts.set(alertId, alert);
    this.emit('alert_resolved', alert);
  }

  /**
   * Perform health checks on all integrations
   */
  private async performHealthChecks(): Promise<void> {
    const integrations = Array.from(this.healthData.values());
    
    for (const integration of integrations) {
      try {
        await this.checkIntegrationHealth(integration);
      } catch (error) {
        console.error(`Health check failed for ${integration.name}:`, error);
      }
    }
  }

  /**
   * Check health of a specific integration
   */
  private async checkIntegrationHealth(integration: IntegrationHealth): Promise<void> {
    const now = new Date();
    const timeSinceLastSync = now.getTime() - integration.lastSync.getTime();
    const maxSyncAge = 60 * 60 * 1000; // 1 hour
    
    // Calculate health score based on various factors
    let healthScore = 100;
    let status: IntegrationHealth['status'] = 'healthy';
    
    // Factor 1: Time since last sync
    if (timeSinceLastSync > maxSyncAge) {
      healthScore -= 20;
      status = 'warning';
    }
    
    // Factor 2: Error rate
    if (integration.errorRate > 10) {
      healthScore -= 30;
      status = 'error';
    } else if (integration.errorRate > 5) {
      healthScore -= 15;
      status = 'warning';
    }
    
    // Factor 3: Response time
    if (integration.responseTime > this.config.responseTimeThreshold) {
      healthScore -= 20;
      if (status === 'healthy') status = 'warning';
    }
    
    // Factor 4: Uptime
    if (integration.uptime < this.config.uptimeThreshold) {
      healthScore -= 25;
      status = 'error';
    }
    
    // Update health data
    integration.healthScore = Math.max(0, healthScore);
    integration.status = status;
    integration.lastHealthCheck = now;
    
    this.healthData.set(integration.id, integration);
    this.emit('health_check_completed', integration);
    
    // Check alert conditions
    this.checkAlertConditions(integration.id, 'health_check', integration);
  }

  /**
   * Update integration health based on sync operation
   */
  private updateIntegrationHealth(integrationId: string, operation: SyncOperation): void {
    const health = this.healthData.get(integrationId);
    if (!health) return;
    
    // Update last sync time
    if (operation.status === 'completed') {
      health.lastSync = operation.completedAt || new Date();
    }
    
    // Update records processed
    health.recordsProcessed += operation.recordsProcessed;
    
    // Calculate success rate (simplified - in real implementation, this would be more sophisticated)
    const recentOperations = this.getSyncOperations(integrationId, 10);
    const successfulOps = recentOperations.filter(op => op.status === 'completed').length;
    health.syncSuccess = recentOperations.length > 0 ? (successfulOps / recentOperations.length) * 100 : 100;
    
    // Calculate error rate
    const failedOps = recentOperations.filter(op => op.status === 'failed').length;
    health.errorRate = recentOperations.length > 0 ? (failedOps / recentOperations.length) * 100 : 0;
    
    // Update response time (if available)
    if (operation.duration) {
      health.responseTime = operation.duration;
    }
    
    this.healthData.set(integrationId, health);
  }

  /**
   * Collect and store performance metrics
   */
  private collectMetrics(): void {
    const metrics = this.getPerformanceMetrics();
    this.metricsHistory.push(metrics);
    
    // Clean up old metrics
    const cutoffDate = new Date(Date.now() - this.config.metricsRetentionDays * 24 * 60 * 60 * 1000);
    this.metricsHistory = this.metricsHistory.filter(m => m.timestamp >= cutoffDate);
    
    this.emit('metrics_collected', metrics);
  }

  /**
   * Check alert conditions and trigger alerts if necessary
   */
  private checkAlertConditions(integrationId: string, eventType: string, data: any): void {
    const rules = Array.from(this.alertRules.values()).filter(rule => 
      rule.enabled && (!rule.integrationId || rule.integrationId === integrationId)
    );
    
    for (const rule of rules) {
      if (this.shouldTriggerAlert(rule, eventType, data)) {
        this.triggerAlert(rule, integrationId, data);
      }
    }
  }

  /**
   * Check if an alert should be triggered
   */
  private shouldTriggerAlert(rule: AlertRule, eventType: string, data: any): boolean {
    // Check cooldown period
    if (rule.lastTriggered) {
      const timeSinceLastTrigger = Date.now() - rule.lastTriggered.getTime();
      const cooldownMs = rule.cooldownPeriod * 60 * 1000;
      if (timeSinceLastTrigger < cooldownMs) {
        return false;
      }
    }
    
    // Check condition based on rule type
    switch (rule.condition) {
      case 'error_rate > threshold':
        return eventType === 'health_check' && data.errorRate > rule.threshold;
      
      case 'response_time > threshold':
        return eventType === 'health_check' && data.responseTime > rule.threshold;
      
      case 'sync_failed':
        return eventType === 'sync_operation_updated' && data.status === 'failed';
      
      case 'health_score < threshold':
        return eventType === 'health_check' && data.healthScore < rule.threshold;
      
      case 'uptime < threshold':
        return eventType === 'health_check' && data.uptime < rule.threshold;
      
      default:
        return false;
    }
  }

  /**
   * Trigger an alert
   */
  private triggerAlert(rule: AlertRule, integrationId: string, data: any): void {
    const alert: Alert = {
      id: this.generateId(),
      ruleId: rule.id,
      integrationId,
      severity: rule.severity,
      title: rule.name,
      message: this.generateAlertMessage(rule, data),
      timestamp: new Date(),
      acknowledged: false,
      resolved: false,
      metadata: { data, rule: rule.condition }
    };
    
    this.activeAlerts.set(alert.id, alert);
    
    // Update rule
    rule.lastTriggered = new Date();
    rule.triggerCount++;
    this.alertRules.set(rule.id, rule);
    
    this.emit('alert_triggered', alert);
    console.warn(`Alert triggered: ${alert.title} for integration ${integrationId}`);
  }

  /**
   * Generate alert message based on rule and data
   */
  private generateAlertMessage(rule: AlertRule, data: any): string {
    switch (rule.condition) {
      case 'error_rate > threshold':
        return `Error rate (${data.errorRate.toFixed(1)}%) exceeds threshold (${rule.threshold}%)`;
      
      case 'response_time > threshold':
        return `Response time (${data.responseTime}ms) exceeds threshold (${rule.threshold}ms)`;
      
      case 'sync_failed':
        return `Sync operation failed: ${data.type} sync`;
      
      case 'health_score < threshold':
        return `Health score (${data.healthScore}) below threshold (${rule.threshold})`;
      
      case 'uptime < threshold':
        return `Uptime (${data.uptime.toFixed(1)}%) below threshold (${rule.threshold}%)`;
      
      default:
        return `Alert condition met: ${rule.condition}`;
    }
  }

  /**
   * Generate unique ID
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Export singleton instance
export const integrationMonitoringService = IntegrationMonitoringService.getInstance();